import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import Sidebar from "@/components/Sidebar";
import {
  Menu,
  ChevronRight,
  TrendingDown,
  Clock,
  Package,
  FileText,
  Truck,
  CheckCircle2,
  Search,
  AlertCircle,
  Cpu,
  AlertTriangle,
  Wrench,
  BadgePercent,
  CircleDollarSign,
  Boxes,
  ShieldAlert,
  X
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Quote status config
const statusConfig = {
  pending: { label: "Pending", color: "text-yellow-500", bgColor: "bg-yellow-500/10", borderColor: "border-yellow-500/20", icon: Clock },
  in_review: { label: "In Review", color: "text-blue-500", bgColor: "bg-blue-500/10", borderColor: "border-blue-500/20", icon: Search },
  quote_sent: { label: "Quote Sent", color: "text-purple-500", bgColor: "bg-purple-500/10", borderColor: "border-purple-500/20", icon: FileText },
  shipped: { label: "Shipped", color: "text-orange-500", bgColor: "bg-orange-500/10", borderColor: "border-orange-500/20", icon: Truck },
  delivered: { label: "Delivered", color: "text-green-500", bgColor: "bg-green-500/10", borderColor: "border-green-500/20", icon: CheckCircle2 }
};

// Service request status config
const srStatusConfig = {
  open: { label: "Open", color: "text-red-400", bgColor: "bg-red-500/10", borderColor: "border-red-500/20" },
  in_progress: { label: "In Progress", color: "text-yellow-400", bgColor: "bg-yellow-500/10", borderColor: "border-yellow-500/20" },
  resolved: { label: "Resolved", color: "text-green-400", bgColor: "bg-green-500/10", borderColor: "border-green-500/20" }
};

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { businessProfile } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("assets");
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setActiveTab(params.get("tab") === "quotes" ? "quotes" : "assets");
  }, [location.search]);

  // Assets state
  const [assets, setAssets] = useState([]);
  const [assetStats, setAssetStats] = useState({ total_assets: 0, total_savings: 0, avg_depreciation: 0, licenses_expiring_soon: 0 });
  const [assetsLoading, setAssetsLoading] = useState(true);

  // Quotes state
  const [myRequests, setMyRequests] = useState([]);
  const [quoteStats, setQuoteStats] = useState({ total_requests: 0, pending_count: 0, total_savings: 0 });
  const [quotesLoading, setQuotesLoading] = useState(true);

  // Service request modal state
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [issueDescription, setIssueDescription] = useState("");
  const [submittingIssue, setSubmittingIssue] = useState(false);
  const [serviceRequests, setServiceRequests] = useState([]);

  useEffect(() => {
    fetchAssets();
    fetchMyRequests();
    fetchServiceRequests();
  }, []);

  const fetchAssets = async () => {
    try {
      const res = await axios.get(`${API}/assets/my-assets`, { withCredentials: true });
      setAssets(res.data.assets);
      setAssetStats(res.data.stats);
    } catch (error) {
      console.error("Error fetching assets:", error);
    } finally {
      setAssetsLoading(false);
    }
  };

  const fetchMyRequests = async () => {
    try {
      const res = await axios.get(`${API}/my-requests`, { withCredentials: true });
      setMyRequests(res.data.requests);
      setQuoteStats(res.data.stats);
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setQuotesLoading(false);
    }
  };

  const fetchServiceRequests = async () => {
    try {
      const res = await axios.get(`${API}/service-requests/my-requests`, { withCredentials: true });
      setServiceRequests(res.data.service_requests);
    } catch (error) {
      console.error("Error fetching service requests:", error);
    }
  };

  const openReportModal = (asset) => {
    setSelectedAsset(asset);
    setIssueDescription("");
    setReportModalOpen(true);
  };

  const handleSubmitIssue = async () => {
    if (!issueDescription.trim()) {
      toast.error("Please describe the issue");
      return;
    }
    setSubmittingIssue(true);
    try {
      await axios.post(`${API}/service-requests`, {
        asset_id: selectedAsset.asset_id,
        asset_name: selectedAsset.name,
        issue_description: issueDescription.trim()
      }, { withCredentials: true });
      toast.success("Issue reported successfully. Our team will be in touch.");
      setReportModalOpen(false);
      fetchServiceRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit issue");
    } finally {
      setSubmittingIssue(false);
    }
  };

  // Get service requests for a specific asset
  const getAssetServiceRequests = (assetId) =>
    serviceRequests.filter(sr => sr.asset_id === assetId);

  const hasAssets = assets.length > 0;
  const hasRequests = myRequests.length > 0;
  const avgDeliveryDays = hasRequests
    ? Math.round(myRequests.reduce((sum, r) => sum + (r.delivery_days || 15), 0) / myRequests.length)
    : 0;

  return (
    <div className="min-h-screen bg-background">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        currentPath={location.pathname}
      />

      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 glass border-b border-border">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 hover:bg-white/5 rounded-lg"
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h1 className="font-heading text-xl font-semibold">
                  {activeTab === "assets" ? "My Assets" : "My Quotes"}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {businessProfile?.company_name || "Complete your profile"}
                </p>
              </div>
            </div>
            {businessProfile?.verification_status === "pending" && (
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFD60A]/10 border border-[#FFD60A]/20 text-sm">
                <Clock className="w-4 h-4 text-[#FFD60A]" />
                <span className="text-[#FFD60A]">Verification Pending</span>
              </div>
            )}
          </div>

        </header>

        <main className="p-4 space-y-4">

          {/* ============ MY ASSETS TAB ============ */}
          {activeTab === "assets" && (
            <>
              {/* CTA Banner */}
              <div className="bg-gradient-to-r from-[#007AFF] to-[#0055CC] rounded-xl px-5 py-3 flex items-center justify-between gap-4">
                <div>
                  <h2 className="font-heading font-semibold text-base text-foreground">
                    {hasAssets ? "Configure another solution?" : "No assets yet — get started"}
                  </h2>
                  <p className="text-muted-foreground text-xs">Assets are added automatically once a quote is delivered.</p>
                </div>
                <Button
                  onClick={() => navigate("/configure")}
                  className="bg-white text-[#007AFF] hover:bg-white/90 font-semibold px-5 h-9 text-sm whitespace-nowrap shrink-0"
                  data-testid="configure-cta"
                >
                  Start Configuring
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>

              {/* Empty state */}
              {!assetsLoading && !hasAssets && (
                <div className="bg-card border border-border rounded-xl p-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mx-auto mb-6">
                    <Package className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="font-heading font-semibold text-xl mb-2">No assets yet</h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    Assets appear here once your quote requests are marked as delivered.
                  </p>
                  <Button
                    onClick={() => navigate("/configure")}
                    className="bg-[#007AFF] hover:bg-[#0055CC]"
                    data-testid="empty-configure-btn"
                  >
                    Configure Now
                  </Button>
                </div>
              )}

              {/* Stats Row */}
              {hasAssets && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="bg-card border border-border rounded-xl px-4 py-3" data-testid="stat-total-assets">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#007AFF]/10">
                        <Boxes className="w-4 h-4 text-[#007AFF]" />
                      </div>
                      <p className="text-xs text-muted-foreground">Total Assets</p>
                    </div>
                    <p className="text-2xl font-bold font-heading">{assetStats.total_assets}</p>
                  </div>

                  <div className="bg-card border border-border rounded-xl px-4 py-3" data-testid="stat-total-savings">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#00FF88]/10">
                        <CircleDollarSign className="w-4 h-4 text-[#00FF88]" />
                      </div>
                      <p className="text-xs text-muted-foreground">Total Savings</p>
                    </div>
                    <p className="text-xl font-bold font-heading">₹{Math.round(assetStats.total_savings).toLocaleString()}</p>
                  </div>

                  <div className="bg-card border border-border rounded-xl px-4 py-3" data-testid="stat-avg-depreciation">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-orange-500/10">
                        <BadgePercent className="w-4 h-4 text-orange-500" />
                      </div>
                      <p className="text-xs text-muted-foreground">Avg Depreciation</p>
                    </div>
                    <p className="text-2xl font-bold font-heading">{assetStats.avg_depreciation}%</p>
                  </div>

                  <div className="bg-card border border-border rounded-xl px-4 py-3" data-testid="stat-licenses-expiring">
                    <div className="flex items-center gap-2 mb-1">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${assetStats.licenses_expiring_soon > 0 ? "bg-red-500/10" : "bg-secondary"}`}>
                        <ShieldAlert className={`w-4 h-4 ${assetStats.licenses_expiring_soon > 0 ? "text-red-400" : "text-muted-foreground"}`} />
                      </div>
                      <p className="text-xs text-muted-foreground">Expiring Soon</p>
                    </div>
                    <p className="text-2xl font-bold font-heading">{assetStats.licenses_expiring_soon}</p>
                  </div>
                </div>
              )}

              {/* Asset List */}
              {hasAssets && (
                <div className="space-y-2">
                  <h2 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wide">Asset Inventory</h2>
                  {assetsLoading ? (
                    <div className="space-y-4">
                      {[1, 2].map(i => <div key={i} className="skeleton h-40 rounded-xl" />)}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {[...assets]
                        .sort((a, b) => {
                          const appleNames = ['macbook', 'ipad', 'mac studio', 'mac mini', 'imac'];
                          const aIsApple = appleNames.some(n => a.name.toLowerCase().includes(n));
                          const bIsApple = appleNames.some(n => b.name.toLowerCase().includes(n));
                          if (aIsApple && !bIsApple) return -1;
                          if (!aIsApple && bIsApple) return 1;
                          return 0;
                        })
                        .map((asset, idx) => {
                        const assetSRs = getAssetServiceRequests(asset.asset_id);
                        return (
                          <div
                            key={asset.asset_id}
                            className="bg-card border border-border rounded-xl px-4 py-3"
                            data-testid={`asset-${asset.asset_id}`}
                          >
                            <div className="flex items-center gap-4">
                              {/* Left: Name + license */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="font-heading font-semibold text-sm">{asset.name}</h3>
                                  {asset.quantity > 1 && (
                                    <span className="text-xs bg-secondary px-2 py-0.5 rounded-full text-muted-foreground shrink-0">
                                      ×{asset.quantity}
                                    </span>
                                  )}
                                  {asset.has_mdm && (
                                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#007AFF]/10 text-[#007AFF] border border-[#007AFF]/20 shrink-0">
                                      <CheckCircle2 className="w-3 h-3" />
                                      EPM/MDM
                                    </span>
                                  )}
                                </div>
                                {assetSRs.length > 0 && (
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    {assetSRs.slice(0, 2).map(sr => {
                                      const srCfg = srStatusConfig[sr.status] || srStatusConfig.open;
                                      return (
                                        <span key={sr.sr_id} className={`text-xs px-2 py-0.5 rounded-full font-medium ${srCfg.bgColor} ${srCfg.color} border ${srCfg.borderColor}`}>
                                          {sr.issue_description.length > 30 ? sr.issue_description.slice(0, 30) + '…' : sr.issue_description} · {srCfg.label}
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>

                              {/* Right: inline stats */}
                              <div className="flex items-center gap-5 shrink-0 text-right">
                                <div className="hidden md:block">
                                  <p className="text-xs text-muted-foreground">Purchased</p>
                                  <p className="text-xs font-medium">
                                    {new Date(asset.purchase_date).toLocaleDateString("en-AE", { day: "numeric", month: "short", year: "numeric" })}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">Value</p>
                                  <p className="text-sm font-heading font-bold text-[#007AFF]">
                                    ₹{Math.round(asset.purchase_value).toLocaleString()}
                                  </p>
                                </div>
                                <div className="hidden sm:block">
                                  <p className="text-xs text-muted-foreground mb-1">Depreciation</p>
                                  <div className="flex items-center gap-1.5">
                                    <div className="w-16 h-1 bg-secondary rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-orange-500 rounded-full"
                                        style={{ width: `${asset.depreciation_pct}%` }}
                                      />
                                    </div>
                                    <span className="text-xs font-medium text-orange-400">{asset.depreciation_pct}%</span>
                                  </div>
                                </div>
                                <button
                                  onClick={() => openReportModal(asset)}
                                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
                                  data-testid={`report-issue-${asset.asset_id}`}
                                >
                                  <Wrench className="w-3.5 h-3.5" />
                                  Report issue
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* ============ MY QUOTES TAB ============ */}
          {activeTab === "quotes" && (
            <>
              {/* Empty state */}
              {!quotesLoading && !hasRequests && (
                <div className="bg-card border border-border rounded-xl p-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mx-auto mb-6">
                    <FileText className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="font-heading font-semibold text-xl mb-2">No quote requests yet</h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    Configure a solution to request your first quote.
                  </p>
                  <Button
                    onClick={() => navigate("/configure")}
                    className="bg-[#007AFF] hover:bg-[#0055CC]"
                    data-testid="quotes-configure-btn"
                  >
                    Configure Now
                  </Button>
                </div>
              )}

              {/* Quote stats */}
              {hasRequests && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="bg-card border border-border rounded-xl px-4 py-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#007AFF]/10">
                        <FileText className="w-4 h-4 text-[#007AFF]" />
                      </div>
                      <p className="text-xs text-muted-foreground">Total Requests</p>
                    </div>
                    <p className="text-2xl font-bold font-heading">{quoteStats.total_requests}</p>
                  </div>
                  <div className="bg-card border border-border rounded-xl px-4 py-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-yellow-500/10">
                        <Clock className="w-4 h-4 text-yellow-500" />
                      </div>
                      <p className="text-xs text-muted-foreground">Pending</p>
                    </div>
                    <p className="text-2xl font-bold font-heading">{quoteStats.pending_count}</p>
                  </div>
                  <div className="bg-card border border-border rounded-xl px-4 py-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#00FF88]/10">
                        <TrendingDown className="w-4 h-4 text-[#00FF88]" />
                      </div>
                      <p className="text-xs text-muted-foreground">Total Savings</p>
                    </div>
                    <p className="text-xl font-bold font-heading">₹{Math.round(quoteStats.total_savings).toLocaleString()}</p>
                  </div>
                  <div className="bg-card border border-border rounded-xl px-4 py-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-orange-500/10">
                        <Truck className="w-4 h-4 text-orange-500" />
                      </div>
                      <p className="text-xs text-muted-foreground">Avg. Delivery</p>
                    </div>
                    <p className="text-2xl font-bold font-heading">{avgDeliveryDays} days</p>
                  </div>
                </div>
              )}

              {/* Quotes list */}
              {hasRequests && (
                <div className="space-y-2">
                  <h2 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wide">Your Quote Requests</h2>
                  {quotesLoading ? (
                    <div className="space-y-2">
                      {[1, 2, 3].map(i => <div key={i} className="skeleton h-14 rounded-xl" />)}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {myRequests.map(request => {
                        const status = statusConfig[request.status] || statusConfig.pending;
                        const StatusIcon = status.icon;
                        return (
                          <div
                            key={request.request_id}
                            className="bg-card border border-border rounded-xl px-4 py-3 hover:border-[#007AFF]/20 transition-colors"
                            data-testid={`request-${request.request_id}`}
                          >
                            <div className="flex items-center gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="font-heading font-semibold text-sm">
                                    {request.item_name || request.bundle_name}
                                  </h3>
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status.bgColor} ${status.color} border ${status.borderColor} shrink-0`}>
                                    <StatusIcon className="w-3 h-3" />
                                    {status.label}
                                  </span>
                                  {request.include_mdm && (
                                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#007AFF]/10 text-[#007AFF] border border-[#007AFF]/20 shrink-0">
                                      <CheckCircle2 className="w-3 h-3" />
                                      EPM/MDM
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  Qty {request.quantity || 1}
                                  {request.delivery_days ? ` · ${request.delivery_days} days delivery` : ""}
                                  {" · "}{new Date(request.created_at).toLocaleDateString("en-AE", { day: "numeric", month: "short", year: "numeric" })}
                                </p>
                              </div>
                              <div className="text-right shrink-0">
                                <p className="text-sm font-heading font-bold text-[#007AFF]">
                                  ₹{Math.round(request.subtotal || request.bundle_price || 0).toLocaleString()}
                                </p>
                                {request.savings > 0 && (
                                  <p className="text-xs text-[#00FF88]">
                                    Save ₹{Math.round(request.savings).toLocaleString()}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Report Issue Modal */}
      <Dialog open={reportModalOpen} onOpenChange={setReportModalOpen}>
        <DialogContent className="bg-card border-border sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center">
                <Wrench className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <DialogTitle>Report an Issue</DialogTitle>
                <DialogDescription className="text-xs mt-0.5">
                  {selectedAsset?.name}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Describe the issue</label>
              <Textarea
                placeholder="e.g. Screen flickering intermittently, battery draining quickly..."
                value={issueDescription}
                onChange={(e) => setIssueDescription(e.target.value)}
                rows={4}
                className="bg-secondary border-border resize-none"
                data-testid="issue-description-input"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Our support team will review your request and follow up within 24 hours.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReportModalOpen(false)} disabled={submittingIssue}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitIssue}
              disabled={submittingIssue || !issueDescription.trim()}
              className="bg-[#007AFF] hover:bg-[#0055CC]"
              data-testid="submit-issue-btn"
            >
              {submittingIssue ? (
                <div className="w-4 h-4 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Wrench className="w-4 h-4 mr-2" />
              )}
              Submit Issue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
